import { useState } from 'react';
import { FALLBACK_PLANS } from '@/constants/plans';
import { settingsAPI } from '@/lib/api';
import { userQualifiesForAuditCredit } from '@/lib/plan-access';
import AuditCreditBadge from '@/components/billing/AuditCreditBadge';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Check, CreditCard, ExternalLink, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const PLAN_RANK = {
  none: 0,
  audit: 1,
  poa: 2,
  monitor: 3,
  guard: 4,
};

function formatMoney(cents, currency = 'usd') {
  if (cents == null) return '—';
  const amount = Number(cents) / 100;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: (currency || 'usd').toUpperCase(),
  }).format(amount);
}

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function checkoutUrl(baseUrl, email) {
  if (!baseUrl) return null;
  const url = new URL(baseUrl);
  if (email) {
    url.searchParams.set('prefilled_email', email);
  }
  return url.toString();
}

function planAccessLabel(planId, billing) {
  const currentRank = PLAN_RANK[billing?.plan] ?? 0;
  const targetRank = PLAN_RANK[planId] ?? 0;

  if (billing?.bypasses_plan_check) {
    return 'included';
  }
  if (!billing?.plan_active) {
    return 'upgrade';
  }
  if (billing.plan === planId) {
    return 'current';
  }
  if (currentRank >= targetRank) {
    return 'included';
  }
  return 'upgrade';
}

function PlanCard({ plan, billing, userEmail, auditCreditEligible }) {
  const access = planAccessLabel(plan.id, billing);
  const isHighlighted = plan.highlighted;
  const isMonitorUpgrade = plan.id === 'monitor' && access === 'upgrade' && auditCreditEligible;
  const [starting, setStarting] = useState(false);

  const openCheckout = async (interval = 'monthly', fallbackUrl) => {
    setStarting(true);
    try {
      const res = await settingsAPI.createCheckoutSession({
        plan: plan.id,
        interval,
        return_origin: window.location.origin,
      });
      if (res.data?.url) {
        if (res.data.audit_credit_applied) {
          toast.success('Applying your $27 audit credit — first month is $270.');
        }
        window.location.href = res.data.url;
        return;
      }
      throw new Error('Missing checkout URL');
    } catch (err) {
      const target = checkoutUrl(fallbackUrl, userEmail);
      if (target) {
        window.open(target, '_blank', 'noopener,noreferrer');
        return;
      }
      toast.error(err.response?.data?.message || err.message || 'Could not start checkout');
    } finally {
      setStarting(false);
    }
  };

  return (
    <Card
      className={`flex flex-col border-border/80 bg-card ${
        isHighlighted ? 'ring-1 ring-primary/40 shadow-[0_0_24px_hsl(188_100%_55%_/_0.08)]' : ''
      }`}
    >
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div>
            <CardTitle className="text-base font-semibold">{plan.name}</CardTitle>
            <CardDescription className="mt-1 text-xs">{plan.description}</CardDescription>
          </div>
          {access === 'current' && (
            <Badge className="shrink-0 bg-primary/15 text-primary border-primary/30">Current</Badge>
          )}
          {access === 'included' && (
            <Badge variant="outline" className="shrink-0">Included</Badge>
          )}
          {isHighlighted && access === 'upgrade' && (
            <Badge className="shrink-0 grad-accent text-white border-0">Popular</Badge>
          )}
        </div>
        <div className="mt-3">
          {isMonitorUpgrade ? (
            <div className="space-y-1">
              <div className="flex flex-wrap items-baseline gap-2">
                <span className="text-lg font-medium text-muted-foreground line-through decoration-red-500/70">
                  $297/mo
                </span>
                <span className="text-2xl font-bold tracking-tight text-foreground">$270/mo</span>
                <AuditCreditBadge />
              </div>
              {plan.price_yearly_label && (
                <p className="text-xs text-muted-foreground">
                  or {plan.price_yearly_label} <span className="text-primary/80">(2 months free)</span>
                </p>
              )}
              <p className="text-[11px] text-red-400/90">Your $27 audit credit — first month only</p>
            </div>
          ) : (
            <>
              <p className="text-2xl font-bold tracking-tight">{plan.price_label}</p>
              {plan.price_yearly_label && (
                <p className="text-xs text-muted-foreground mt-0.5">
                  or {plan.price_yearly_label} <span className="text-primary/80">(2 months free)</span>
                </p>
              )}
            </>
          )}
        </div>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col gap-4 pt-0">
        <ul className="space-y-2 text-sm text-muted-foreground flex-1">
          {(plan.features || []).map((feature) => (
            <li key={feature} className="flex gap-2">
              <Check className="h-4 w-4 shrink-0 text-primary mt-0.5" />
              <span>{feature}</span>
            </li>
          ))}
        </ul>

        <div className="flex flex-col gap-2">
          {access === 'upgrade' && !plan.checkout_url && (
            <p className="text-xs text-center text-amber-500/90">
              Test payment link not configured. Add STRIPE_LINK_* to protego-etl .env (Stripe Dashboard → Test mode).
            </p>
          )}
          {access === 'upgrade' && plan.checkout_url && (
            <div className="relative">
              {isMonitorUpgrade && (
                <div className="absolute -top-3 right-2 z-10 rotate-6">
                  <AuditCreditBadge />
                </div>
              )}
              <Button
                type="button"
                className={isHighlighted ? 'grad-accent text-white border-0 w-full' : 'w-full'}
                variant={isHighlighted ? 'default' : 'outline'}
                disabled={starting}
                onClick={() => openCheckout('monthly', plan.checkout_url)}
              >
                {starting ? 'Starting checkout…' : (
                  isMonitorUpgrade ? (
                    <>
                      Subscribe <span className="line-through opacity-70 mx-1">$297</span> $270/mo
                    </>
                  ) : (
                    plan.type === 'one_time' ? `Pay ${plan.price_label}` : `Subscribe ${plan.price_label}`
                  )
                )}
                <ExternalLink className="ml-2 h-4 w-4" />
              </Button>
            </div>
          )}
          {access === 'upgrade' && plan.checkout_url_yearly && (
            <Button
              type="button"
              variant="outline"
              className="w-full"
              disabled={starting}
              onClick={() => openCheckout('yearly', plan.checkout_url_yearly)}
            >
              {starting ? 'Starting checkout…' : `Pay yearly — ${plan.price_yearly_label}`}
              <ExternalLink className="ml-2 h-4 w-4" />
            </Button>
          )}
          {access === 'current' && (
            <p className="text-xs text-center text-muted-foreground">This is your active plan.</p>
          )}
          {access === 'included' && (
            <p className="text-xs text-center text-muted-foreground">Covered by your current subscription.</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default function PlansBillingTab({ billingData, loading, userEmail, paywall = false, onBillingRefresh, onPlanAccessReload, user }) {
  const [syncing, setSyncing] = useState(false);

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-28 w-full" />
        <div className="grid gap-4 md:grid-cols-2">
          <Skeleton className="h-64" />
          <Skeleton className="h-64" />
        </div>
      </div>
    );
  }

  const billing = billingData?.billing;
  const plans = (billingData?.plans?.length ? billingData.plans : FALLBACK_PLANS);
  const payments = billingData?.payments || [];
  const auditCreditEligible = userQualifiesForAuditCredit(user, billing);

  const handleSyncPayments = async () => {
    setSyncing(true);
    try {
      const res = await settingsAPI.syncPayments();
      const billing = res.data?.billing;

      if (onBillingRefresh) {
        await onBillingRefresh();
      }

      if (billing?.plan_active && onPlanAccessReload) {
        toast.success(res.data?.message || 'Plan updated — loading your access…');
        await onPlanAccessReload(billing);
        return;
      }

      toast.success(res.data?.message || 'Plan updated');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not sync payment');
    } finally {
      setSyncing(false);
    }
  };

  const subscription = billingData?.active_subscription;

  return (
    <div className="space-y-6">
      <Card className="bg-card border-border/80">
        <CardHeader>
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <CreditCard className="h-4 w-4 text-primary" />
            {paywall ? 'Subscription required' : 'Current plan'}
          </CardTitle>
          {paywall && (
            <CardDescription className="text-xs">
              Your account is active but no plan is selected yet. Pick a plan below to access monitoring.
            </CardDescription>
          )}
        </CardHeader>
        <CardContent className="space-y-3">
          {billing?.bypasses_plan_check ? (
            <div className="rounded-lg border border-primary/30 bg-primary/5 px-4 py-3 text-sm">
              <p className="font-medium text-foreground">Team / admin access</p>
              <p className="text-xs text-muted-foreground mt-1">
                Your account bypasses subscription checks. All dashboard features are unlocked.
              </p>
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-3">
                <p className="text-lg font-semibold">{billing?.plan_label || 'Free'}</p>
                <Badge variant={billing?.plan_active ? 'default' : 'secondary'}>
                  {billing?.plan_active ? 'Active' : 'Inactive'}
                </Badge>
              </div>
              {billing?.plan_expires_at && (
                <p className="text-sm text-muted-foreground">
                  {billing.plan_active ? 'Renews / expires' : 'Expired'}: {formatDate(billing.plan_expires_at)}
                </p>
              )}
              {subscription && (
                <p className="text-xs text-muted-foreground">
                  Subscription status: <span className="capitalize">{subscription.status}</span>
                  {subscription.cancel_at_period_end ? ' · cancels at period end' : ''}
                </p>
              )}
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="mt-2"
                disabled={syncing}
                onClick={handleSyncPayments}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
                {syncing ? 'Syncing…' : 'I paid — refresh my plan'}
              </Button>
              <p className="text-[11px] text-muted-foreground">
                Use this if Stripe did not redirect back after payment.
              </p>
            </>
          )}
        </CardContent>
      </Card>

      <div>
        <h2 className="text-base font-semibold mb-1">Available plans</h2>
        <p className="text-sm text-muted-foreground mb-4">
          Select a plan to pay securely via Stripe. Your access updates after payment is confirmed.
        </p>
        <div className="grid gap-4 md:grid-cols-2">
          {plans.map((plan) => (
            <PlanCard
              key={plan.id}
              plan={plan}
              billing={billing}
              userEmail={userEmail}
              auditCreditEligible={auditCreditEligible}
            />
          ))}
        </div>
      </div>

      {payments.length > 0 && (
        <Card className="bg-card border-border/80">
          <CardHeader>
            <CardTitle className="text-base font-semibold">Payment history</CardTitle>
            <CardDescription className="text-xs">Recent charges on your account</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border/60 text-left text-muted-foreground">
                    <th className="pb-2 pr-4 font-medium">Date</th>
                    <th className="pb-2 pr-4 font-medium">Amount</th>
                    <th className="pb-2 pr-4 font-medium">Status</th>
                    <th className="pb-2 font-medium">Type</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((payment) => (
                    <tr key={payment.id} className="border-b border-border/40 last:border-0">
                      <td className="py-2.5 pr-4">{formatDate(payment.paid_at)}</td>
                      <td className="py-2.5 pr-4">{formatMoney(payment.amount, payment.currency)}</td>
                      <td className="py-2.5 pr-4 capitalize">{payment.status}</td>
                      <td className="py-2.5 text-muted-foreground">
                        {(payment.billing_reason || 'payment').replace(/_/g, ' ')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
